/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import SegmentModel from 'cio/models/segment';
import { MetricTypes, Entities } from 'cio/utils/metrics/common';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { SegmentMembershipMetric } from 'cio/utils/metrics/models/segment-membership-metric';
import { tracked } from '@glimmer/tracking';
import config from 'cio/config/environment';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import { stringIncludes } from 'cio/utils/string';
import MetricsService from 'cio/services/metrics';
import MasqService from 'cio/services/masq';
import Ember from 'ember';
import { Grouping } from 'cio/utils/metrics/resolution';
import { TimeRange } from 'cio/utils/time';
import ShortSegmentModel from 'cio/models/short-segment';
import DS from 'ember-data';

const PER_PAGE = 50;
const DEBOUNCE_DELAY = config.APP.shortDelay;
const SEGMENT_RELOAD_INTERVAL = config.APP.segmentReloadInterval;

interface DashboardPageSegmentsCardChartContainerArgs {
  segmentId: string | null;
  timeRange: TimeRange;
  grouping: Grouping;
  segments: ShortSegmentModel[];
  onSelectSegment: (segment: SegmentModel) => void;
  isLoading: boolean;
  isUpdatingSettings: boolean;
  onTrackNavigate: (data: Record<string, any>) => unknown;
  onTrackChartHover: (data: Record<string, any>) => unknown;
}

export default class DashboardPageSegmentsCardChartContainer extends Component<DashboardPageSegmentsCardChartContainerArgs> {
  @service masq!: MasqService;
  @service('metrics') metricsService!: MetricsService;
  @service store!: DS.Store;

  @tracked hasData = false;

  get shouldShowLink() {
    return !!this.args.segmentId && this.hasData;
  }

  get selectedShortSegment() {
    return this.args.segments.find((s) => s.id === this.args.segmentId);
  }

  @tracked selectedSegment: SegmentModel | null = null;

  get initialSelectOptions() {
    return this.args.segments.slice(0, PER_PAGE);
  }

  @tracked virtualSelectPage = 1;

  // The list for dropdowns only contains "short" segment models - we need to
  // fetch the full ones to get the current state
  fetchSelectedSegment = task({ restartable: true }, async () => {
    let { segmentId } = this.args;

    if (segmentId == null) {
      return;
    }

    this.selectedSegment = await this.store.findRecord('segment', segmentId);
  });

  onSegmentChanged = task({ restartable: true }, async () => {
    await this.fetchSelectedSegment.perform();

    if (this.selectedSegment?.isBuilding) {
      this.metricsService.clearMetrics(MetricTypes.SegmentMembershipMetric, Entities.SEGMENT, this.selectedSegment.id);
      this.pollForSegmentBuildFinish.perform();
    }
  });

  virtualSelectQuery(searchText: string, page: number) {
    let start = PER_PAGE * (page - 1);

    return this.args.segments
      .filter((segment) => stringIncludes(segment.name, searchText))
      .slice(start, start + PER_PAGE);
  }

  chartOptions = {
    tooltip: {
      headerClasses: 'text-xs font-normal',
    },
  };

  pollForSegmentBuildFinish = task(async () => {
    await timeout(SEGMENT_RELOAD_INTERVAL);
    let segment = this.selectedSegment;

    if (segment) {
      try {
        await segment.reload();

        if (segment.isBuilding && !Ember.testing) {
          this.pollForSegmentBuildFinish.perform();
        }
      } catch {
        // ignore
      }
    }
  });

  selectOnScrollEnd = task({ restartable: true }, async (searchText: string) => {
    this.virtualSelectPage = this.virtualSelectPage + 1;

    return this.virtualSelectQuery(searchText, this.virtualSelectPage);
  });

  paginatedSelectQuery = task({ restartable: true }, async (searchText: string) => {
    this.virtualSelectPage = 1;

    // Add a debounce duration so that we don't perform the search as the user
    // is typing
    await timeout(DEBOUNCE_DELAY);
    return this.virtualSelectQuery(searchText, this.virtualSelectPage);
  });

  // This action is required to stop triggering the LinkTo when clicking on the
  // FlyPowerSelect component
  @action
  stopBubbling(e: Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  @action
  onDataFetched(metric: SegmentMembershipMetric) {
    let seriesData = metric?.total ?? [];

    this.hasData = seriesData.some((p) => p.y !== 0);
  }

  @action
  onTrackNavigate() {
    this.args.onTrackNavigate({
      card: 'segments',
      destination: 'segment',
    });
  }

  @action
  onTrackChartHover(time: number) {
    this.args.onTrackChartHover({
      model: this.selectedSegment,
      time,
    });
  }
}
